import React, {useState} from 'react';
import {Box, Link, Stack, Typography} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {FILE_UPLOAD_MAX_SIZE_MB} from 'utils';
import {useTranslation} from 'react-i18next';

type FileUploadAreaBoxProps = {
    error?: string | null;
    onUploadFiles: (files: File[]) => void;
    disableEdit?: boolean;
    accept?: string;
    multiple?: boolean;
};

export const FileUploadAreaBox: React.FC<FileUploadAreaBoxProps> = ({
    error,
    onUploadFiles,
    disableEdit,
    accept,
    multiple = true,
}) => {
    const [dragOver, setDragOver] = useState(false);

    const handleDragOver: React.DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        setDragOver(false);
    };

    const handleDrop: React.DragEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        setDragOver(false);
        const files = event.dataTransfer.files;
        if (files) {
            const filesArray = Array.from(files);
            onUploadFiles(filesArray);
        }
    };

    const handleFileInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        if (event.target.files !== null) {
            const filesArray = Array.from(event.target.files);
            onUploadFiles(filesArray);
        }
        event.target.value = '';
    };

    const {t} = useTranslation(['supervising', 'common']);

    return (
        <>
            <Box
                width='100%'
                border='1px dashed var(--mui-palette-divider)'
                style={{background: dragOver ? 'var(--mui-palette-background-paper)' : 'none'}}
                position='relative'
                padding='24px'
                borderRadius='4px'
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                component='div'
                marginBottom='16px'
                sx={{
                    pointerEvents: disableEdit ? 'none' : 'auto',
                    opacity: disableEdit ? 0.6 : 1,
                }}
            >
                <Stack
                    justifyContent='center'
                    alignItems='center'
                    direction='column'
                    gap='8px'
                >
                    <UploadFileIcon color='primary' />
                    <Typography
                        variant='regular1'
                        color='var(--mui-palette-text-primary)'
                        sx={{textAlign: 'center'}}
                    >
                        <Link>
                            <label
                                htmlFor='fileUpload'
                                style={{
                                    cursor: 'pointer',
                                    color: 'var(--mui-palette-primary-main)',
                                }}
                            >
                                {t('fileUploadArea.clickToDownload')}
                            </label>
                        </Link>
                        <input
                            multiple={multiple}
                            id='fileUpload'
                            type='file'
                            hidden
                            onChange={handleFileInputChange}
                            accept={accept}
                        />{' '}
                        {t('fileUploadArea.dragFilesIntoContainer').toLowerCase()}
                    </Typography>
                    <Typography
                        variant='regular2'
                        color='var(--mui-palette-text-secondary)'
                    >
                        {`${t('maxShort').toLowerCase()}. ${FILE_UPLOAD_MAX_SIZE_MB} MB`}
                    </Typography>
                    {error && (
                        <Typography
                            style={{color: 'var(--mui-palette-error-main)', textAlign: 'center'}}
                            variant='caption'
                            fontSize='14px'
                        >
                            {error}
                        </Typography>
                    )}
                </Stack>
            </Box>
        </>
    );
};
